import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Card,
  CardMedia,
  IconButton,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  Modal,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Snackbar,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { Delete, CloudUpload, Description, ExpandMore } from "@mui/icons-material";
import PageHeader from "../../components/PageHeader";
import CrudManager from "../../utils/Crud-Manager/CrudManager";
import schema from "./Data/ScreenShotCRUDConfig.json";
import csvRecordsSchema from "./Data/csvRecords.json";
import ImageCard from "../../components/ImageCard";

import useApi from "../../utils/useApi";
import { getAPI, getHost } from "../../Global";
import global from "../../config/Global.json";

const ScreenShot = () => {
  const [images, setImages] = useState([]);
  const [csvFiles, setCsvFiles] = useState([]);
  const [storedCsvFiles, setStoredCsvFiles] = useState([]);
  const [storedImages, setStoredImages] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");
  const [submitMessage, setSubmitMessage] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState("success");
  const [groupTitle, setGroupTitle] = useState("");
  const [showTransactions, setShowTransactions] = useState(false);
  const [subscriptionNo, setSubscriptionNo] = useState("");
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    transaction_utr: "",
    amount: "",
    transaction_date: "",
    comparison_status: "Failed",
    comparison_status_reason: "No matching UTR",
    group_title: "",
    subscription_no: "",
    payee_name: "",
    reference_amount: "0"
  });
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [correctScreenshotModal, setCorrectScreenshotModal] = useState(false);
  const [selectedScreenshotId, setSelectedScreenshotId] = useState(null);
  const [correctScreenshotData, setCorrectScreenshotData] = useState({
    correct_subscription_no: '',
    correct_group_title: ''
  });
  const [previewModal, setPreviewModal] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [pendingCsvFiles, setPendingCsvFiles] = useState([]);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
  const [screenshotToDelete, setScreenshotToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteTransactionModal, setDeleteTransactionModal] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState(null);
  const [deleteTransactionLoading, setDeleteTransactionLoading] = useState(false);
  const { Get, Post, Delete, Put, Patch } = useApi();

  const handleResetScreenshot = async (screenshotId) => {
    try {
      await Post("resetScreenshot", { screenshot_id: screenshotId });
      setSubmitMessage('Screenshot reset successfully!');
      setAlertSeverity('success');
      setShowAlert(true);
    } catch (error) {
      setSubmitMessage('Error resetting screenshot');
      setAlertSeverity('error');
      setShowAlert(true);
      console.error('Error resetting screenshot:', error);
    }
  };

  const handleCorrectScreenshot = (screenshotId) => {
    setSelectedScreenshotId(screenshotId);
    setCorrectScreenshotModal(true);
  };

  const handleCorrectScreenshotSubmit = async () => {
    if (!correctScreenshotData.correct_subscription_no) {
      setSubmitMessage('Correct subscription number is required');
      setAlertSeverity('error');
      setShowAlert(true);
      return;
    }

    try {
      const payload = {
        screenshot_id: selectedScreenshotId,
        correct_subscription_no: parseInt(correctScreenshotData.correct_subscription_no),
        ...(correctScreenshotData.correct_group_title && {
          correct_group_title: correctScreenshotData.correct_group_title
        })
      };

      const response = await Post("correctScreenshot", payload);
      
      setSubmitMessage(response.data.message || 'Screenshot corrected successfully!');
      setAlertSeverity('success');
      setShowAlert(true);
      
      setCorrectScreenshotModal(false);
      setCorrectScreenshotData({ correct_subscription_no: '', correct_group_title: '' });
      setSelectedScreenshotId(null);
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Error correcting screenshot';
      setSubmitMessage(errorMessage);
      setAlertSeverity('error');
      setShowAlert(true);
      console.error('Error correcting screenshot:', error);
    }
  };

  const handleEditTransaction = (transaction) => {
    setEditData({
      id: transaction.id,
      transaction_utr: transaction.transaction_utr || '',
      amount: transaction.amount || '',
      reference_amount: transaction.reference_amount || '',
      transaction_date: transaction.transaction_date ? transaction.transaction_date.split('T')[0] : '',
      group_title: transaction.group_title || '',
      subscription_no: transaction.subscription_no || '',
      payee_name: transaction.payee_name || '',
      payer_name: transaction.payer_name || '',
      comparison_status: transaction.comparison_status || '',
      comparison_status_reason: transaction.comparison_status_reason || '',
      transaction_verified: transaction.transaction_verified || false,
      is_migrated: transaction.is_migrated || false,
    });
    setEditModal(true);
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleEditSubmit = async () => {
    try {
      await Patch('transactionComparison', editData.id, editData);
      setSubmitMessage('Transaction comparison updated successfully!');
      setAlertSeverity('success');
      setShowAlert(true);
      setEditModal(false);
      setEditData(null);
      fetchTransactions();
    } catch (error) {
      setSubmitMessage('Error updating transaction comparison');
      setAlertSeverity('error');
      setShowAlert(true);
      console.error('Error updating:', error);
    }
  };

  const handleCorrectScreenshotCancel = () => {
    setCorrectScreenshotModal(false);
    setCorrectScreenshotData({ correct_subscription_no: '', correct_group_title: '' });
    setSelectedScreenshotId(null);
  };

  const handleDeleteScreenshot = (screenshot) => {
    setScreenshotToDelete(screenshot);
    setDeleteConfirmModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!screenshotToDelete) return;
    setDeleteLoading(true);
    try {
      await Patch('screenshot', screenshotToDelete.id, { can_replace: true });
      setSubmitMessage('Screenshot marked for replacement and hidden.');
      setAlertSeverity('success');
      setShowAlert(true);
      setDeleteConfirmModal(false);
      setScreenshotToDelete(null);
    } catch (error) {
      setSubmitMessage('Error deleting screenshot');
      setAlertSeverity('error');
      setShowAlert(true);
      console.error('Error deleting screenshot:', error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmModal(false);
    setScreenshotToDelete(null);
  };

  const handleDeleteTransaction = (transaction) => {
    setTransactionToDelete(transaction);
    setDeleteTransactionModal(true);
  };

  const handleConfirmDeleteTransaction = async () => {
    if (!transactionToDelete) return;
    setDeleteTransactionLoading(true);
    try {
      await Delete('transactionComparison', transactionToDelete.id);
      setSubmitMessage('Transaction comparison deleted successfully!');
      setAlertSeverity('success');
      setShowAlert(true);
      setDeleteTransactionModal(false);
      setTransactionToDelete(null);
      fetchTransactions();
    } catch (error) {
      setSubmitMessage('Error deleting transaction comparison');
      setAlertSeverity('error');
      setShowAlert(true);
      console.error('Error deleting transaction:', error);
    } finally {
      setDeleteTransactionLoading(false);
    }
  };

  const handleCancelDeleteTransaction = () => {
    setDeleteTransactionModal(false);
    setTransactionToDelete(null);
  };

  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImages((prev) => [
          ...prev,
          {
            id: Date.now() + Math.random(),
            name: file.name,
            url: e.target.result,
            file: file,
          },
        ]);
      };
      reader.readAsDataURL(file);
    });
  };




  const handleCsvUpload = (event) => {
    const files = Array.from(event.target.files);
    files.forEach((file) => {
      setCsvFiles((prev) => [
        ...prev,
        {
          id: Date.now() + Math.random(),
          name: file.name,
          size: (file.size / 1024).toFixed(2) + " KB",
          file: file,
          uploadDate: new Date().toISOString().split("T")[0],
        },
      ]);
    });
  };

  const handleDeleteImage = (id) => {
    setImages((prev) => prev.filter((img) => img.id !== id));
  };





  const handleViewCsv = (csvUrl) => {
    const processedUrl = csvUrl.replace('http://127.0.0.1/', getHost() + '/');
    window.open(processedUrl, '_blank');
  };

  const handleDownloadCsv = (csvUrl, filename) => {
    const processedUrl = csvUrl.replace('http://127.0.0.1/', getHost() + '/');
    const link = document.createElement('a');
    link.href = processedUrl;
    link.download = filename || 'file.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleSubmitImages = async () => {
    try {
      const userResponse = await Get("isSuperUser");
      const isDhruv = userResponse.is_dhruv_inv;
      
      const promises = images.map(async (image) => {
        const formData = new FormData();
        formData.append('qr', image.file);
        formData.append('is_dhruv', isDhruv ? 'True' : 'False');
        
        return await Post("qrCode", formData);
      });
      
      await Promise.all(promises);
      setImages([]);
      fetchStoredImages();
      
      console.log('Images submitted with is_dhruv:', isDhruv);
    } catch (error) {
      console.error("Error submitting images:", error);
    }
  };

  const handleSubmitCsv = async () => {
    if (csvFiles.length === 0) return;
    setPreviewLoading(true);
    try {
      const userResponse = await Get('isSuperUser');
      const isDhruv = userResponse.is_dhruv_inv;
      // Preview only the first file (or all — collect all rows)
      const allRows = [];
      let hasErrors = false;
      for (const csvFile of csvFiles) {
        const formData = new FormData();
        formData.append('csv', csvFile.file);
        const token = JSON.parse(localStorage.getItem('authToken'))?.access;
        const res = await fetch(
          `${global.api.host}${global.api.csvPreview}`,
          { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData }
        );
        const data = await res.json();
        if (data.error) throw new Error(data.error);
        allRows.push({ fileName: csvFile.name, isDhruv, ...data });
        if (data.has_errors) hasErrors = true;
      }
      setPreviewData({ files: allRows, hasErrors });
      setPendingCsvFiles(csvFiles.map(f => ({ ...f, isDhruv })));
      setPreviewModal(true);
    } catch (error) {
      setSubmitMessage('Error previewing CSV: ' + (error.message || 'Unknown error'));
      setAlertSeverity('error');
      setShowAlert(true);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleConfirmUpload = async () => {
    setPreviewModal(false);
    try {
      const promises = pendingCsvFiles.map(async (csvFile) => {
        const formData = new FormData();
        formData.append('csv', csvFile.file);
        formData.append('is_dhruv', csvFile.isDhruv ? 'True' : 'False');
        formData.append('upload_date', csvFile.uploadDate);
        return await Post('csv', formData);
      });
      await Promise.all(promises);
      setSubmitMessage(`Successfully uploaded ${pendingCsvFiles.length} CSV file${pendingCsvFiles.length > 1 ? 's' : ''}`);
      setAlertSeverity('success');
      setShowAlert(true);
      setCsvFiles([]);
      setPendingCsvFiles([]);
      fetchStoredCsvFiles();
    } catch (error) {
      setSubmitMessage('Error uploading CSV files. Please try again.');
      setAlertSeverity('error');
      setShowAlert(true);
    }
  };

  const handleViewImage = (imageUrl) => {
    console.log('Original imageUrl:', imageUrl);
    const processedUrl = imageUrl.includes('http://127.0.0.1/') 
      ? imageUrl.replace('http://127.0.0.1/', getHost() + '/')
      : imageUrl;
    console.log('Processed URL:', processedUrl);
    setSelectedImage(processedUrl);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedImage("");
  };

  const fetchStoredCsvFiles = async () => {
    try {
      const userResponse = await Get("isSuperUser");
      const isDhruv = userResponse.is_dhruv_inv;

      const response = await Get("csv");
      const allCsvFiles = response.results || response;

      const filteredFiles = allCsvFiles.filter(
        (csv) => csv.is_dhruv === isDhruv
      );
      setStoredCsvFiles(filteredFiles);
    } catch (error) {
      console.error("Error fetching CSV files:", error);
    }
  };

  const fetchStoredImages = async () => {
    try {
      const userResponse = await Get("isSuperUser");
      const isDhruv = userResponse.is_dhruv_inv;

      const response = await Get("qrCode");
      const allImages = response.results || response;

      const filteredImages = allImages.filter(
        (img) => img.is_dhruv === isDhruv
      );
      setStoredImages(filteredImages);
    } catch (error) {
      console.error("Error fetching images:", error);
    }
  };



  const fetchTransactions = async () => {
    if (!groupTitle || !subscriptionNo) return;
    
    setLoading(true);
    try {
      const response = await Get(`${getAPI('transactionComparison')}?group_title=${encodeURIComponent(groupTitle)}&subscription_no=${encodeURIComponent(subscriptionNo)}`);
      setTransactions(response.results || response);
      setShowTransactions(true);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setSubmitMessage('Error fetching transactions');
      setAlertSeverity('error');
      setShowAlert(true);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Tab' && event.target.name === 'subscriptionNo' && groupTitle && subscriptionNo) {
      event.preventDefault();
      fetchTransactions();
    }
  };

  const handleGroupTitleChange = (e) => {
    setGroupTitle(e.target.value.toUpperCase());
    setShowTransactions(false);
  };

  const handleSubscriptionChange = (e) => {
    setSubscriptionNo(e.target.value);
    setShowTransactions(false);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmitTransaction = async () => {
    try {
      const payload = {
        transaction_utr: formData.transaction_utr,
        amount: parseFloat(formData.amount),
        transaction_date: formData.transaction_date,
        comparison_status: formData.comparison_status,
        comparison_status_reason: formData.comparison_status_reason,
        group_title: formData.group_title,
        subscription_no: parseInt(formData.subscription_no),
        payee_name: formData.payee_name,
        reference_amount: parseFloat(formData.reference_amount),
        manually: true
      };
      
      await Post("transactionComparison", payload);
      
      setSubmitMessage('Transaction comparison created successfully!');
      setAlertSeverity('success');
      setShowAlert(true);
      
      setFormData({
        transaction_utr: "",
        amount: "",
        transaction_date: "",
        comparison_status: "Failed",
        comparison_status_reason: "No matching UTR",
        group_title: "",
        subscription_no: "",
        payee_name: "",
        reference_amount: "0"
      });
      setShowCreateForm(false);
    } catch (error) {
      setSubmitMessage('Error creating transaction comparison');
      setAlertSeverity('error');
      setShowAlert(true);
      console.error('Error submitting transaction:', error);
    }
  };

  useEffect(() => {
    fetchStoredCsvFiles();
    fetchStoredImages();
  }, []);

  return (
    <Box sx={{ display: "flex" }}>
      <Box
        sx={{
          flexGrow: 1,
          marginTop: 0,
          marginLeft: 15,
          marginRight: 15,
          marginBottom: 15,
        }}
      >
        <PageHeader pageTitle="Payment Management" />

        <Paper elevation={3} sx={{ padding: 3, marginTop: 2 }}>
          <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 3 }}>
            <Tab label="Images" />
            <Tab label="CSV Files" />
            <Tab label="Screenshot Verification" />
          </Tabs>

          {tabValue === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Upload Image
              </Typography>

              <Button
                variant="contained"
                component="label"
                startIcon={<CloudUpload />}
                sx={{ mb: 3 }}
              >
                Upload Image
                <input
                  type="file"
                  hidden
                  multiple
                  accept="image/*"
                  onChange={handleImageUpload}
                />
              </Button>

              {images.length > 0 && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                    Uploaded Images ({images.length})
                  </Typography>

                  <Grid container spacing={2}>
                    {images.map((image) => (
                      <Grid item xs={12} sm={6} md={4} key={image.id}>
                        <Card>
                          <CardMedia
                            component="img"
                            height="200"
                            image={image.url}
                            alt={image.name}
                            sx={{ objectFit: "contain" }}
                          />
                          <Box
                            sx={{
                              p: 1,
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Typography variant="body2" noWrap>
                              {image.name}
                            </Typography>
                            <IconButton
                              color="error"
                              onClick={() => handleDeleteImage(image.id)}
                              size="small"
                            >
                              <Delete />
                            </IconButton>
                          </Box>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>

                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmitImages}
                    sx={{ mt: 2 }}
                  >
                    Submit Image
                  </Button>
                </>
              )}

              {storedImages.length > 0 && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                    Stored Images ({storedImages.length})
                  </Typography>

                  <div className="row g-3">
                    {storedImages.map((image) => (
                      <ImageCard
                        key={image.id}
                        image={image}
                        onView={handleViewImage}
                        getHost={getHost}
                      />
                    ))}
                  </div>
                </>
              )}

            </Box>
          )}

          {tabValue === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Upload CSV Files
              </Typography>

              <Button
                variant="contained"
                component="label"
                startIcon={<CloudUpload />}
                sx={{ mb: 3 }}
              >
                Upload CSV Files
                <input
                  type="file"
                  hidden
                  multiple
                  accept=".csv"
                  onChange={handleCsvUpload}
                />
              </Button>

              {csvFiles.length > 0 && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                    Uploaded CSV Files ({csvFiles.length})
                  </Typography>

                  {Object.entries(
                    csvFiles.reduce((acc, csv) => {
                      const date = csv.uploadDate;
                      if (!acc[date]) acc[date] = [];
                      acc[date].push(csv);
                      return acc;
                    }, {})
                  ).map(([date, files]) => (
                    <Accordion key={date} sx={{ mb: 1 }}>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="subtitle1">
                          {new Date(date).toLocaleDateString()} ({files.length}{" "}
                          files)
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <List>
                          {files.slice(0, 10).map((csv) => (
                            <ListItem
                              key={csv.id}
                              sx={{
                                border: 1,
                                borderColor: "grey.300",
                                mb: 1,
                                borderRadius: 2,
                                backgroundColor: "#fafafa",
                                boxShadow: 1,
                                "&:hover": {
                                  backgroundColor: "#f0f0f0",
                                  boxShadow: 2,
                                },
                              }}
                            >
                              <Description
                                sx={{ mr: 2, color: "primary.main" }}
                              />
                              <ListItemText
                                primary={
                                  <Typography
                                    variant="body1"
                                    fontWeight="medium"
                                  >
                                    {csv.name}
                                  </Typography>
                                }
                                secondary={
                                  <Box
                                    sx={{ display: "flex", gap: 2, mt: 0.5 }}
                                  >
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      Size: {csv.size}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      Date:{" "}
                                      {new Date(
                                        csv.uploadDate
                                      ).toLocaleDateString("en-US", {
                                        year: "numeric",
                                        month: "short",
                                        day: "numeric",
                                      })}
                                    </Typography>
                                  </Box>
                                }
                              />
                            </ListItem>
                          ))}
                          {files.length > 10 && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ textAlign: "center", mt: 1 }}
                            >
                              Showing 10 of {files.length} files
                            </Typography>
                          )}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  ))}

                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmitCsv}
                    disabled={previewLoading}
                    sx={{ mt: 2 }}
                  >
                    {previewLoading ? 'Previewing...' : 'Submit CSV Files'}
                  </Button>
                </>
              )}

              {storedCsvFiles.length > 0 && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                    Stored CSV Files ({storedCsvFiles.length})
                  </Typography>

                  {Object.entries(
                    storedCsvFiles
                      .sort((a, b) => new Date(b.upload_date || b.created_at) - new Date(a.upload_date || a.created_at))
                      .reduce((acc, csv) => {
                        const date = new Date(csv.upload_date || csv.created_at || new Date()).toISOString().split('T')[0];
                        if (!acc[date]) acc[date] = [];
                        acc[date].push(csv);
                        return acc;
                      }, {})
                  ).map(([date, files]) => (
                    <Accordion key={date} sx={{ mb: 1 }}>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="subtitle1">
                          {new Date(date).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })} ({files.length} files)
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <List>
                          {files.map((csv) => (
                            <ListItem
                              key={csv.id}
                              sx={{
                                border: 1,
                                borderColor: "grey.300",
                                mb: 1,
                                borderRadius: 2,
                                backgroundColor: "#fafafa",
                                boxShadow: 1,
                                '&:hover': {
                                  backgroundColor: "#f0f0f0",
                                  boxShadow: 2,
                                },
                              }}
                            >
                              <Description sx={{ mr: 2, color: "primary.main" }} />
                              <ListItemText 
                                primary={
                                  <Typography
                                    variant="body1"
                                    fontWeight="medium"
                                  >
                                    {csv.csv
                                      ? csv.csv.split("/").pop()
                                      : "CSV File"}
                                  </Typography>
                                }
                                secondary={
                                  <Typography variant="body2" color="text.secondary">
                                    ID: {csv.id} • Uploaded: {new Date(csv.upload_date || csv.created_at).toLocaleDateString('en-US', {
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </Typography>
                                }
                              />
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleViewCsv(csv.csv)}
                                sx={{
                                  minWidth: '80px',
                                  fontSize: '12px',
                                  '&:hover': {
                                    backgroundColor: 'primary.light',
                                    color: 'white'
                                  }
                                }}
                              >
                                View
                              </Button>
                            </ListItem>
                          ))}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </>
              )}

              <h1>All records</h1>
              <CrudManager
                data={csvRecordsSchema}
                createRequired={false}
                editRequired={false}
                searchRequired={true}
                actionBtnName={false}
                hideDelete={true}
                createFormTitle=""
                updateFormTitle=""
              />
            </Box>
          )}

          {tabValue === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Screenshot Verification
              </Typography>
              
              {showCreateForm && (
                <Box sx={{ mt: 3, mb: 4, p: 3, border: 1, borderColor: 'grey.300', borderRadius: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Create Transaction Comparison
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Transaction UTR"
                        name="transaction_utr"
                        value={formData.transaction_utr}
                        onChange={handleFormChange}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Amount"
                        name="amount"
                        type="number"
                        value={formData.amount}
                        onChange={handleFormChange}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Transaction Date"
                        name="transaction_date"
                        type="date"
                        value={formData.transaction_date}
                        onChange={handleFormChange}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Group Title"
                        name="group_title"
                        value={formData.group_title}
                        onChange={handleFormChange}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Subscription No"
                        name="subscription_no"
                        type="number"
                        value={formData.subscription_no}
                        onChange={handleFormChange}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Payee Name"
                        name="payee_name"
                        value={formData.payee_name}
                        onChange={handleFormChange}
                        size="small"
                        required
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSubmitTransaction}
                        sx={{ mr: 2 }}
                      >
                        Submit Transaction
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => setShowCreateForm(false)}
                      >
                        Cancel
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              )}

              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Search Transaction Comparison
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                  <TextField
                    label="Group Title"
                    value={groupTitle}
                    onChange={handleGroupTitleChange}
                    size="small"
                    sx={{ minWidth: 200 }}
                  />
                  <TextField
                    label="Subscription No"
                    name="subscriptionNo"
                    value={subscriptionNo}
                    onChange={handleSubscriptionChange}
                    onKeyDown={handleKeyPress}
                    size="small"
                    sx={{ minWidth: 200 }}
                  />
                </Box>

                {loading && (
                  <Typography>Loading transactions...</Typography>
                )}

                {showTransactions && transactions.length > 0 && (
                  <>
                    <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
                      <Button
                        variant="contained"
                        color="success"
                        onClick={() => setShowCreateForm(true)}
                      >
                        Create Transaction Comparison
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => fetchTransactions()}
                      >
                        Refresh
                      </Button>
                    </Box>
                    <TableContainer component={Paper} sx={{ mt: 2 }}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#17202A' }}>
                          <TableCell sx={{ color: '#FFF' }}>UTR</TableCell>
                          <TableCell sx={{ color: '#FFF' }}>Group Title</TableCell>
                          <TableCell sx={{ color: '#FFF' }}>Subscription No</TableCell>
                          <TableCell sx={{ color: '#FFF' }}>Amount</TableCell>
                          <TableCell sx={{ color: '#FFF' }}>Transaction Date</TableCell>
                          <TableCell sx={{ color: '#FFF' }}>Payment App</TableCell>
                          <TableCell sx={{ color: '#FFF' }}>Screenshot</TableCell>
                          <TableCell sx={{ color: '#FFF' }}>Status</TableCell>
                          <TableCell sx={{ color: '#FFF' }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {transactions.map((transaction, index) => (
                          <TableRow key={transaction.id || index}>
                            <TableCell>{transaction.transaction_utr}</TableCell>
                            <TableCell>{transaction.group_title}</TableCell>
                            <TableCell>{transaction.subscription_no}</TableCell>
                            <TableCell>{transaction.amount}</TableCell>
                            <TableCell>{transaction.transaction_date}</TableCell>
                            <TableCell>{transaction.payment_app}</TableCell>
                            <TableCell>
                              {transaction.screenshot_url ? (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => handleViewImage(transaction.screenshot_url)}
                                >
                                  View
                                </Button>
                              ) : (
                                'No Image'
                              )}
                            </TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  borderRadius: '20px',
                                  backgroundColor: transaction.comparison_status === 'Success' ? '#4caf50' : '#f44336',
                                  color: 'white',
                                  fontWeight: 'bold',
                                  width: '80px',
                                  height: '30px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                {transaction.comparison_status}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleEditTransaction(transaction)}
                                sx={{ mr: 1 }}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="contained"
                                size="small"
                                color="error"
                                onClick={() => handleDeleteTransaction(transaction)}
                              >
                                Delete
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  </>
                )}
              </Box>
            </Box>
          )}


        </Paper>

        <Paper elevation={3} sx={{ padding: 3, marginTop: 2 }}>
           <Typography variant="h6" gutterBottom>
               Screenshots uploaded by customer in mobile app
              </Typography>
          <CrudManager
            hideDelete={true}
            editRequired={false}
            data={schema}
            createRequired={false}
            actionBtnName="Add New Subscriptiotn"
            createFormTitle="Add Subscription"
            updateFormTitle="Update Subscription"
            onViewImage={handleViewImage}
            onResetScreenshot={handleResetScreenshot}
            onCorrectScreenshot={handleCorrectScreenshot}
            onDeleteScreenshot={handleDeleteScreenshot}
          />
        </Paper>

        <Modal open={modalOpen} onClose={handleCloseModal}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 2,
              maxWidth: "90vw",
              maxHeight: "90vh",
              overflow: "auto",
            }}
          >
            <img
              src={selectedImage}
              alt="Screenshot"
              style={{ width: "100%", height: "auto" }}
            />
          </Box>
        </Modal>

        {/* Edit TransactionComparison Modal */}
        <Modal open={editModal} onClose={() => setEditModal(false)}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              bgcolor: 'background.paper',
              boxShadow: 24,
              p: 4,
              borderRadius: 2,
              minWidth: 500,
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
          >
            <Typography variant="h6" gutterBottom>
              Edit Transaction Comparison
            </Typography>
            {editData && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Transaction UTR" name="transaction_utr" value={editData.transaction_utr} onChange={handleEditChange} size="small" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Amount" name="amount" type="number" value={editData.amount} onChange={handleEditChange} size="small" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Reference Amount" name="reference_amount" type="number" value={editData.reference_amount} onChange={handleEditChange} size="small" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Transaction Date" name="transaction_date" type="date" value={editData.transaction_date} onChange={handleEditChange} size="small" InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Group Title" name="group_title" value={editData.group_title} onChange={handleEditChange} size="small" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Subscription No" name="subscription_no" type="number" value={editData.subscription_no} onChange={handleEditChange} size="small" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Payer Name" name="payer_name" value={editData.payer_name} onChange={handleEditChange} size="small" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Payee Name" name="payee_name" value={editData.payee_name} onChange={handleEditChange} size="small" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Comparison Status" name="comparison_status" value={editData.comparison_status} onChange={handleEditChange} size="small" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Status Reason" name="comparison_status_reason" value={editData.comparison_status_reason} onChange={handleEditChange} size="small" />
                </Grid>
                <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">Transaction Verified</Typography>
                  <input type="checkbox" name="transaction_verified" checked={editData.transaction_verified} onChange={handleEditChange} />
                </Grid>
                <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">Is Migrated</Typography>
                  <input type="checkbox" name="is_migrated" checked={editData.is_migrated} onChange={handleEditChange} />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button variant="outlined" onClick={() => setEditModal(false)}>Cancel</Button>
                    <Button variant="contained" color="primary" onClick={handleEditSubmit}>Save Changes</Button>
                  </Box>
                </Grid>
              </Grid>
            )}
          </Box>
        </Modal>

        {/* Correct Screenshot Modal */}
        <Modal open={correctScreenshotModal} onClose={handleCorrectScreenshotCancel}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 4,
              borderRadius: 2,
              minWidth: 400,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Correct Screenshot
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Screenshot ID: {selectedScreenshotId}
            </Typography>
            
            <TextField
              fullWidth
              label="Correct Subscription Number *"
              type="number"
              value={correctScreenshotData.correct_subscription_no}
              onChange={(e) => setCorrectScreenshotData(prev => ({
                ...prev,
                correct_subscription_no: e.target.value
              }))}
              sx={{ mb: 2 }}
              required
            />
            
            <TextField
              fullWidth
              label="Correct Group Title (Optional)"
              value={correctScreenshotData.correct_group_title}
              onChange={(e) => setCorrectScreenshotData(prev => ({
                ...prev,
                correct_group_title: e.target.value.toUpperCase()
              }))}
              sx={{ mb: 3 }}
              helperText="Leave empty to use existing group"
            />
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={handleCorrectScreenshotCancel}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleCorrectScreenshotSubmit}
                disabled={!correctScreenshotData.correct_subscription_no}
              >
                Correct Screenshot
              </Button>
            </Box>
          </Box>
        </Modal>

        {/* Delete Transaction Comparison Modal */}
        <Modal open={deleteTransactionModal} onClose={handleCancelDeleteTransaction}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              bgcolor: 'background.paper',
              boxShadow: 24,
              p: 4,
              borderRadius: 2,
              minWidth: 500,
              maxHeight: '85vh',
              overflowY: 'auto',
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ color: '#d32f2f', fontWeight: 'bold' }}>
              Delete Transaction Comparison
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Are you sure you want to delete this transaction comparison? This action cannot be undone.
            </Typography>

            {transactionToDelete && (
              <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Transaction Details
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>UTR:</strong> {transactionToDelete.transaction_utr || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Amount:</strong> ₹{transactionToDelete.amount || '0'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Group:</strong> {transactionToDelete.group_title || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Subscription:</strong> {transactionToDelete.subscription_no || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Payer:</strong> {transactionToDelete.payer_name || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Status:</strong> {transactionToDelete.comparison_status || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Date:</strong> {new Date(transactionToDelete.transaction_date).toLocaleDateString()}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={handleCancelDeleteTransaction}
                disabled={deleteTransactionLoading}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleConfirmDeleteTransaction}
                disabled={deleteTransactionLoading}
              >
                {deleteTransactionLoading ? 'Deleting...' : 'Delete Transaction'}
              </Button>
            </Box>
          </Box>
        </Modal>

        {/* Delete Screenshot Confirmation Modal */}
        <Modal open={deleteConfirmModal} onClose={handleCancelDelete}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              bgcolor: 'background.paper',
              boxShadow: 24,
              p: 4,
              borderRadius: 2,
              minWidth: 500,
              maxHeight: '85vh',
              overflowY: 'auto',
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ color: '#d32f2f', fontWeight: 'bold' }}>
              Delete Screenshot
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Mark this screenshot as replaceable? It will be hidden until a new screenshot is uploaded.
            </Typography>

            {screenshotToDelete && (
              <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Screenshot Preview
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mb: 2,
                    maxHeight: 300,
                    overflow: 'hidden',
                    borderRadius: 1,
                    bgcolor: '#fff',
                    border: '1px solid #e0e0e0',
                  }}
                >
                  <img
                    src={screenshotToDelete.screenshot.includes('http://127.0.0.1/')
                      ? screenshotToDelete.screenshot.replace('http://127.0.0.1/', getHost() + '/')
                      : screenshotToDelete.screenshot}
                    alt="Screenshot to delete"
                    style={{ maxWidth: '100%', maxHeight: '300px', objectFit: 'contain' }}
                  />
                </Box>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Group:</strong> {screenshotToDelete.group_title || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Subscription:</strong> {screenshotToDelete.subscription_no || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Uploaded by:</strong> {screenshotToDelete.added_by_name || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Date:</strong> {new Date(screenshotToDelete.created_at).toLocaleDateString()}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={handleCancelDelete}
                disabled={deleteLoading}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleConfirmDelete}
                disabled={deleteLoading}
              >
                {deleteLoading ? 'Updating...' : 'Mark for Replacement'}
              </Button>
            </Box>
          </Box>
        </Modal>

        {/* CSV Preview Confirmation Modal */}
        <Modal open={previewModal} onClose={() => setPreviewModal(false)}>
          <Box sx={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper', boxShadow: 24, p: 3, borderRadius: 2,
            width: '90vw', maxWidth: 1100, maxHeight: '85vh', overflowY: 'auto'
          }}>
            <Typography variant="h6" gutterBottom>CSV Preview — Confirm Upload</Typography>
            {previewData?.hasErrors && (
              <Alert severity="error" sx={{ mb: 2 }}>
                One or more UTRs are in invalid format (e.g. scientific notation like <strong>6.22E+11</strong>). Fix the CSV before uploading.
              </Alert>
            )}
            {previewData?.files?.map((fileData, fi) => (
              <Box key={fi} sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                  {fileData.fileName} — {fileData.total} rows
                </Typography>
                <TableContainer sx={{ border: '1px solid #e0e0e0', borderRadius: 1, maxHeight: 400 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        {['#', 'UTR', 'Date (Raw)', 'Date (Parsed)', 'Amount', 'Store', 'Status'].map(h => (
                          <TableCell key={h} sx={{ fontWeight: 'bold', bgcolor: '#17202A', color: '#fff' }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {fileData.rows?.map((row) => (
                        <TableRow key={row.row} sx={{ bgcolor: !row.utr_valid ? '#fff3f3' : row.row % 2 === 0 ? '#fafafa' : '#fff' }}>
                          <TableCell>{row.row}</TableCell>
                          <TableCell sx={{ color: !row.utr_valid ? '#d32f2f' : 'inherit', fontWeight: !row.utr_valid ? 'bold' : 'normal' }}>
                            {row.utr || '—'}
                            {!row.utr_valid && <Typography variant="caption" display="block" color="error">Invalid UTR format</Typography>}
                          </TableCell>
                          <TableCell><code>{row.date_raw || '—'}</code></TableCell>
                          <TableCell sx={{ color: !row.date_valid ? '#e65100' : 'inherit' }}>
                            {row.date_parsed || <span style={{ color: '#e65100' }}>Could not parse</span>}
                          </TableCell>
                          <TableCell>{row.amount || '—'}</TableCell>
                          <TableCell>{row.store_name || '—'}</TableCell>
                          <TableCell>{row.status || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ))}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
              <Button variant="outlined" onClick={() => setPreviewModal(false)}>Cancel</Button>
              <Button
                variant="contained" color="primary"
                onClick={handleConfirmUpload}
                disabled={previewData?.hasErrors}
              >
                Confirm & Upload
              </Button>
            </Box>
          </Box>
        </Modal>

        <Snackbar
          open={showAlert}
          autoHideDuration={4000}
          onClose={() => setShowAlert(false)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={() => setShowAlert(false)}
            severity={alertSeverity}
            sx={{ width: "100%" }}
          >
            {submitMessage}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default ScreenShot;
